import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, File, Video, Play } from 'lucide-react';

interface Media {
  id: string;
  url: string;
  name: string;
  type: string;
  mimeType: string;
}

interface MediaViewerProps {
  media: Media[];
  initialIndex?: number;
  onClose: () => void;
}

const MediaViewer: React.FC<MediaViewerProps> = ({ 
  media, 
  initialIndex = 0, 
  onClose 
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  
  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  
  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % media.length);
  };
  
  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + media.length) % media.length);
  };
  
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.touches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    
    const difference = touchStartX - touchEndX;
    const minSwipeDistance = 50;
    
    if (difference > minSwipeDistance) {
      handleNext();
    } else if (difference < -minSwipeDistance) {
      handlePrev();
    }
    
    setTouchStartX(null);
    setTouchEndX(null);
  };
  
  const currentMedia = media[currentIndex];
  
  return (
    <div className="media-viewer-overlay">
      <button className="close-button" onClick={onClose}>
        <X size={24} />
      </button>
      
      <div 
        className="media-viewer-content"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {currentMedia.type === 'image' ? (
          <img 
            src={currentMedia.url} 
            alt={currentMedia.name} 
            className="media-viewer-image"
          />
        ) : currentMedia.type === 'video' ? (
          <video 
            src={currentMedia.url} 
            controls 
            className="media-viewer-video"
          />
        ) : (
          <div className="media-viewer-file">
            <File size={64} />
            <p>{currentMedia.name}</p>
            <a 
              href={currentMedia.url} 
              download 
              className="download-button"
              onClick={(e) => e.stopPropagation()}
            >
              Download
            </a>
          </div>
        )}
      </div>
      
      {media.length > 1 && (
        <>
          <button 
            className="nav-button prev-button" 
            onClick={handlePrev}
          >
            <ChevronLeft size={24} />
          </button>
          <button 
            className="nav-button next-button" 
            onClick={handleNext}
          >
            <ChevronRight size={24} />
          </button>
          <div className="media-counter">
            {currentIndex + 1} / {media.length}
          </div>
        </>
      )}
    </div>
  );
};

export default MediaViewer; 